import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Package,
  Tag,
  MessageSquare,
  Send,
  CheckCircle,
  AlertCircle,
  LogIn,
  MapPin,
  Hash,
} from "lucide-react";
import Card from "../modern-components/ui/Card";
import Button from "../modern-components/ui/Button";
import Badge from "../modern-components/ui/Badge";
import { productAPI, quoteAPI, BASE_URL } from "../services/api";

const RequestQuote = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get("product");

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Form state
  const [quantity, setQuantity] = useState("");
  const [unit, setUnit] = useState("");
  const [notes, setNotes] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [pincode, setPincode] = useState("");

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem("token");
    const userStr = localStorage.getItem("user");

    console.log("Token exists:", !!token);
    console.log("User string:", userStr);

    if (token && userStr) {
      try {
        const userData = JSON.parse(userStr);
        console.log("Parsed user data:", userData);
        console.log("User type:", userData.user_type);

        if (userData.user_type === "customer") {
          setIsAuthenticated(true);
          console.log("User authenticated as customer");
        } else {
          setIsAuthenticated(false);
          setError(
            `Only customers can request quotes. Your account type is: ${userData.user_type}`,
          );
          console.log("User is not a customer:", userData.user_type);
        }
      } catch (err) {
        console.error("Error parsing user data:", err);
        setIsAuthenticated(false);
      }
    } else {
      console.log("No token or user found in localStorage");
      setIsAuthenticated(false);
    }

    if (productId) {
      fetchProduct();
    } else {
      setError("No product selected");
      setLoading(false);
    }
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const response = await productAPI.getProductById(productId);
      if (response.data.success) {
        const productData = response.data.data;
        setProduct(productData);
        setUnit(productData.unit || "");
      } else {
        setError("Product not found");
      }
    } catch (err) {
      setError("Failed to fetch product details");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!quantity || parseFloat(quantity) <= 0) {
      setError("Please enter a valid quantity");
      return;
    }

    if (!deliveryAddress.trim()) {
      setError("Delivery address is required");
      return;
    }

    if (!/^\d{6}$/.test(pincode.trim())) {
      setError("Please enter a valid 6-digit pincode");
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const response = await quoteAPI.createQuote({
        product_id: parseInt(productId),
        quantity: parseFloat(quantity),
        unit: unit,
        customer_notes: notes,
        delivery_address: deliveryAddress.trim(),
        pincode: pincode.trim(),
      });

      if (response.data.success) {
        setSuccess("Quote request submitted successfully!");
        setTimeout(() => {
          navigate("/dashboard?tab=quotes");
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to submit quote request");
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-40 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading product...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - show login prompt
  if (!isAuthenticated && !loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-40 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <Card className="text-center">
            <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <LogIn className="w-10 h-10 text-primary-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Login Required
            </h2>
            <p className="text-gray-600 mb-6">
              Please login with your customer account to request a quote for
              this product.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="primary"
                onClick={() =>
                  navigate(`/login?redirect=/get-quote?product=${productId}`)
                }
                leftIcon={<LogIn className="w-5 h-5" />}
              >
                Login to Continue
              </Button>
              <Button variant="outline" onClick={() => navigate("/products")}>
                Back to Products
              </Button>
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Don't have an account?{" "}
                <button
                  onClick={() => navigate("/register")}
                  className="text-primary-600 hover:text-primary-700 font-semibold"
                >
                  Register here
                </button>
              </p>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (error && !product) {
    return (
      <div className="min-h-screen bg-gray-50 pt-40 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Product Not Found
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate("/products")}>
            Back to Products
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-40 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          leftIcon={<ArrowLeft className="w-5 h-5" />}
          onClick={() => navigate(`/products/${productId}`)}
          className="mb-6 animate-fade-in"
        >
          Back to Product
        </Button>

        {/* Page Header */}
        <div className="mb-8 animate-fade-in">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Request a Quote
          </h1>
          <p className="text-lg text-gray-600">
            Fill in the details below and we'll get back to you with the best
            pricing
          </p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3 animate-slide-up">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-green-900">Success!</h3>
              <p className="text-green-700 text-sm">{success}</p>
            </div>
          </div>
        )}

        {error && product && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 animate-slide-up">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900">Error</h3>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Product Details Card */}
          <div className="animate-slide-up">
            <Card>
              <div className="mb-4">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Product Details
                </h2>
              </div>

              {/* Product Image */}
              <div className="mb-6 rounded-xl overflow-hidden bg-gray-100">
                <img
                  src={
                    product?.image_url
                      ? product.image_url.startsWith("http")
                        ? product.image_url
                        : `${BASE_URL}${product.image_url}`
                      : "https://images.unsplash.com/photo-1587293852726-70cdb56c2866?w=600"
                  }
                  alt={product?.name}
                  className="w-full h-64 object-cover"
                />
              </div>

              {/* Product Info */}
              <div className="space-y-4">
                <div>
                  <Badge variant="gray" className="mb-2">
                    {product?.category?.name || "Uncategorized"}
                  </Badge>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {product?.name}
                  </h3>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <p className="text-gray-600 leading-relaxed">
                    {product?.description || "No description available"}
                  </p>
                </div>

                {/* Price Range */}
                {product?.price_range_min && product?.price_range_max && (
                  <div className="pt-4 border-t border-gray-200">
                    <div className="text-sm text-gray-500 mb-1">
                      Indicative Price Range
                    </div>
                    <div className="text-2xl font-bold text-primary-600">
                      {formatPrice(product.price_range_min)} -{" "}
                      {formatPrice(product.price_range_max)}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      per {product?.unit || "unit"}
                    </div>
                  </div>
                )}

                {/* Product Status */}
                <div className="pt-4 border-t border-gray-200">
                  {product?.is_active ? (
                    <Badge variant="success" size="lg">
                      ✓ Available for Quote
                    </Badge>
                  ) : (
                    <Badge variant="danger" size="lg">
                      Currently Unavailable
                    </Badge>
                  )}
                </div>
              </div>
            </Card>
          </div>

          {/* Quote Request Form */}
          <div className="animate-fade-in">
            <Card>
              <h2 className="text-xl font-bold text-gray-900 mb-6">
                Quote Request Form
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Quantity */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Quantity <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Package className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      placeholder="Enter quantity"
                      required
                      disabled={submitting}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Minimum quantity: 0.01
                  </p>
                </div>

                {/* Unit */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Unit
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Tag className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={unit}
                      onChange={(e) => setUnit(e.target.value)}
                      placeholder="e.g., kg, ton, piece, meter"
                      disabled={submitting}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>

                {/* Delivery Address */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Delivery Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute top-3 left-3 pointer-events-none">
                      <MapPin className="h-5 w-5 text-gray-400" />
                    </div>
                    <textarea
                      rows={3}
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                      placeholder="Enter full delivery address"
                      required
                      disabled={submitting}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>

                {/* Pincode */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Pincode <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Hash className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={pincode}
                      onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="6-digit pincode"
                      required
                      disabled={submitting}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>
                </div>

                {/* Additional Notes */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Additional Requirements / Notes
                  </label>
                  <div className="relative">
                    <div className="absolute top-3 left-3 pointer-events-none">
                      <MessageSquare className="h-5 w-5 text-gray-400" />
                    </div>
                    <textarea
                      rows={5}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Please specify any special requirements, delivery location, timeline, or other details..."
                      disabled={submitting}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Include delivery location, timeline, quality requirements,
                    etc.
                  </p>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 text-lg">ℹ️</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-blue-900 mb-1">
                        What happens next?
                      </h4>
                      <ul className="text-xs text-blue-700 space-y-1">
                        <li>• Our team will review your request</li>
                        <li>
                          • You'll receive competitive pricing within 24-48
                          hours
                        </li>
                        <li>• Compare quotes and place your order</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Submit Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(`/products/${productId}`)}
                    disabled={submitting}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    leftIcon={submitting ? null : <Send className="w-5 h-5" />}
                    disabled={submitting || !product?.is_active}
                    className="flex-1"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Submitting...
                      </>
                    ) : (
                      "Submit Quote Request"
                    )}
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 animate-fade-in">
          <Card className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">
              Quality Assured
            </h3>
            <p className="text-sm text-gray-600">100% certified products</p>
          </Card>

          <Card className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Package className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Fast Delivery</h3>
            <p className="text-sm text-gray-600">Quick dispatch & tracking</p>
          </Card>

          <Card className="text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Tag className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">Best Prices</h3>
            <p className="text-sm text-gray-600">Competitive market rates</p>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RequestQuote;
